/**
 * THE TAX ON AN INVESTMENT PROPERTY WHEN IT IS SOLD, INCLUDING BY THE ESTATE (owner, 21 Oct 2026 — the
 * village review, applied to every module).
 *
 * A projection carries an investment property to the end at its value and never sells it, so "left to
 * the family" counted the whole value — the capital gains tax that falls due when the family (or the
 * estate) sells it was never taken off. Death is not itself a CGT event for the property (ITAA 1997
 * s 128-10): the beneficiary inherits the cost base, and the tax is paid on the sale. This estimates that
 * tax at each year's close as if the property were sold then, with the same single CGT engine every sale
 * in the app uses (`computeDisposalCgt`: 50% discount, the cost base held — or the app's cost-base
 * assumption where none is entered — pre-CGT property exempt, the household's own tax position that year
 * as the stand-in for the beneficiaries', whose rates are not known).
 *
 * It is NOT taken off net worth, which stays the market value of what is owned; only the figure left to
 * the family nets it. A former home being rented out is a main residence within the six-year rule and
 * is treated as the CGT engine treats it.
 */
import { computeDisposalCgt } from '../disposalCgt.ts';
import { resolveDisposalTaxContext } from '../disposalTaxContext.ts';
import type { FactFind } from '../factFindTypes.ts';

const num = (v: unknown) => Number(v) || 0;

/** The capital gains tax the family would pay selling every investment property held, at this point. */
export function estateCgtOnInvestmentProperties(factFind: FactFind | null | undefined, financials?: unknown, disposalDate?: Date | string): number {
    const props = ((factFind?.assets as { investmentProperty?: Array<Record<string, unknown>> } | undefined)?.investmentProperty) || [];
    let total = 0;
    for (const asset of props) {
        const value = num(asset?.value);
        if (!(value > 0)) continue;
        // A former home not rented out is still exempt as a main residence (no other is nominated).
        if (asset?.formerHome === true && !asset?.firstRentedOn) continue;
        const r = computeDisposalCgt({
            taxContext: resolveDisposalTaxContext(factFind as FactFind, financials as never),
            asset: asset as never,
            salePrice: value,
            sellingCosts: value * 0.025,
            originalAssetValue: value,
            disposalDate,
            structures: (factFind as { structures?: unknown } | undefined)?.structures as never,
        });
        total += Math.max(0, num(r.estimatedCGT));
    }
    return Math.round(total);
}

/**
 * THE FAMILY HOME, KEPT AND RENTED OUT (owner, 22 Oct 2026: "Yes agree you can update the CGT aspect … the projection
 * period is just a period.. we dont know how long the clients will realistically live").
 *
 * A plan that keeps the home and lets it (aged care's rentHome, the village's keep-and-rent) carried the home in the
 * projection as the family home, so what the family is left treated it as free of capital gains tax for the whole
 * run. That holds for six years: the main-residence absence rule (ITAA 1997 s118-145) keeps a former home exempt
 * while it is let, if no other home is treated as the main residence. After six years the part of the gain since
 * it was first let that falls beyond six years is taxed when it is sold, including by the estate (s118-185; the
 * cost base resets to its value on the day it was first let, s118-192). This is the granny flat's former-home
 * treatment (formerHomeAbsence, B.CGT.5) applied to the kept home, from the day letting starts: the home as a
 * former home first let on `firstRentedOn`, valued at its value that year, with the household's own tax position
 * standing in for the beneficiaries', as for an investment property. Nil inside six years.
 */
export function estateCgtOnRentedHome(factFind: FactFind | null | undefined, financials: unknown, disposalDate: Date | string | undefined, firstRentedOn: Date | null | undefined): number {
    const home = (factFind as { home?: { value?: unknown; homeGrowthRate?: unknown; ownership?: string } } | undefined)?.home;
    const value = num(home?.value);
    if (!(value > 0) || !firstRentedOn || !Number.isFinite(firstRentedOn.getTime())) return 0;
    const when = disposalDate ? new Date(disposalDate) : new Date();
    const rentedOn = `${firstRentedOn.getFullYear()}-${String(firstRentedOn.getMonth() + 1).padStart(2, '0')}`;
    const asset = {
        id: 'home',
        description: 'Family home',
        value,
        growthRate: home?.homeGrowthRate != null && Number.isFinite(Number(home.homeGrowthRate)) ? Number(home.homeGrowthRate) : undefined,
        ownership: home?.ownership && home.ownership !== 'other_entity' ? home.ownership : 'joint',
        formerHome: true,
        firstRentedOn: rentedOn,
    };
    const r = computeDisposalCgt({
        taxContext: resolveDisposalTaxContext(factFind as FactFind, financials as never),
        asset: asset as never,
        salePrice: value,
        sellingCosts: value * 0.025,
        originalAssetValue: value,
        disposalDate: when,
        // The value is this year's, so the cost-base reset is taken back from this year to the day it was let.
        valuationDate: when,
    });
    return Math.round(Math.max(0, num(r.estimatedCGT)));
}

/** What is left to the family at a projection year: net worth, less the tax on selling the investment properties. */
export function estateOf(row: { netWorth?: unknown; estateCgt?: unknown } | null | undefined): number | null {
    if (!row || row.netWorth == null || !Number.isFinite(Number(row.netWorth))) return null;
    return Number(row.netWorth) - num(row.estateCgt);
}
